"use client"

import React, { ReactNode, Ref } from "react"
import { Select as MUSelect } from "@mui/material"
import FormControl from "@mui/material/FormControl"
import ListSubheader from "@mui/material/ListSubheader"
import MenuItem from "@mui/material/MenuItem"
import { styled } from "@mui/material/styles"
import clsx from "clsx"
import { Absolute, Row } from "ui/components/atoms/_atoms"
import Icon from "ui/components/atoms/icon"
import { ErrorMessage } from "ui/components/atoms/input"
import Spinner from "ui/components/atoms/spinner"
import { Label } from "ui/components/atoms/typography"
import { baseTheme } from "ui/theme"
import { BaseStyles } from "ui/types/base-styles"
import { generateStylesFromProps } from "ui/utils/styles"

import styles from "./Select.module.css"

const StyledFormControl = styled(FormControl)(() => ({
  "width": "100%",
  "heigth": "max-content",
  "display": "block",
  "background": "none",
  "overflow": "hidden",

  ".MuiInputBase-root ": {
    width: "100%",
  },
}))

const StyledSelect = styled(MUSelect)(() => {
  return {
    "borderRadius": baseTheme.variable.borderRadius,
    "padding": "0",

    ".MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      color: baseTheme.color.second,
      minHeight: "45px !important",
      fontSize: baseTheme.font.L,
      lineHeight: "23px",
      fontWeight: baseTheme.font.light,
      paddingLeft: baseTheme.font.XS,
      paddingTop: baseTheme.font.XS,
      paddingBottom: baseTheme.font.XS,
    },
  }
})

export type SelectProps = BaseStyles & {
  label?: string
  name: string
  value?: unknown
  placeholder?: string
  options?: any[] | any[][]
  onChange?: (e: { name: string; value: any }) => void
  disabled?: boolean
  loading?: boolean
  multiple?: boolean
  multipleShow?: number
  icon?: string
  error?: {
    type?: string
    message?: string
  }
  rhf?: object & { ref?: Ref<any> }
}

const Select = ({
  options = [],
  onChange = () => {},
  value,
  placeholder,
  label,
  name,
  disabled,
  error,
  loading,
  icon,
  multiple,
  multipleShow,
  rhf = {},
  className = "",
  style = {},
  ...rest
}: SelectProps & { className?: string; style?: React.CSSProperties }) => {
  const { ref, ...rhfRest } = rhf
  const finalOptions = options.map(el => (Array.isArray(el) ? el : [el, el]))
  const generatedStyles = generateStylesFromProps(rest)

  const handleOnChange = (e: any) => {
    onChange({
      name,
      value: e.target.value,
    })
  }

  const getMultipleValue = (val: string) => {
    return options?.find(el => {
      if (Array.isArray(el)) {
        return el[1] === val
      } else {
        return val
      }
    })
  }

  const wrapperClasses = clsx(
    styles.wrapper,
    {
      [styles.disabled]: disabled,
    },
    className,
  )

  const wrapperStyles = {
    ...generatedStyles,
    ...style,
  }

  const boxClasses = clsx(styles.styledBox, {
    [styles.disabled]: disabled,
    [styles.withIcon]: !!icon,
  })

  const boxStyles = {
    zIndex: 0,
    border: rest.$borderColor ? `1px solid var(--color-${rest.$borderColor})` : undefined,
    background:
      rest.$background && rest.$background !== "none"
        ? `var(--color-${rest.$background})`
        : undefined,
  }

  return (
    <div className={wrapperClasses} style={wrapperStyles}>
      {label && (
        <Label $type="secondary" $marginBottom="XS">
          {label}
        </Label>
      )}
      <div className={boxClasses} style={boxStyles}>
        <StyledFormControl>
          <StyledSelect
            defaultValue={value}
            value={value}
            disabled={disabled || finalOptions.length === 0}
            error={!!error}
            multiple={multiple}
            variant="outlined"
            onChange={handleOnChange}
            renderValue={
              multiple
                ? (selected: unknown) => {
                    const renderSelected = (selected as string[]).slice(0, multipleShow)
                    const otherSelected = (selected as string[]).length - renderSelected.length

                    return (
                      <Row $alignItems="center">
                        {renderSelected.map((el, index) => {
                          const val = getMultipleValue(el)

                          const value = Array.isArray(val) ? val[1] : val
                          const label = Array.isArray(val) ? val[0] : val
                          const icon = Array.isArray(val) ? val[2] : undefined

                          return (
                            <Row
                              key={value}
                              $alignItems="center"
                              $marginRight="XXS"
                              $padding="XXS"
                              $paddingRight="XS"
                              $paddingLeft="XS"
                              $background="background"
                              $borderRadius="borderRadius"
                            >
                              {icon && <Icon icon={icon} $size="L" $marginRight="XXS" />}
                              {label}
                            </Row>
                          )
                        })}
                        {otherSelected > 0 && (
                          <Row
                            $alignItems="center"
                            $marginRight="XXS"
                            $padding="XXS"
                            $paddingRight="XS"
                            $paddingLeft="XS"
                            $background="background"
                            $borderRadius="borderRadius"
                          >
                            +{otherSelected}
                          </Row>
                        )}
                      </Row>
                    ) as ReactNode
                  }
                : undefined
            }
            {...rhfRest}
            inputRef={ref}
          >
            {finalOptions.map(option => {
              const value = Array.isArray(option) ? option[1] : option
              const label = Array.isArray(option) ? option[0] : option
              const icon = Array.isArray(option) ? option[2] : undefined

              if (value === "#")
                return <ListSubheader key={`subheader-${value}`}>{label}</ListSubheader>

              return (
                <MenuItem key={value} value={value}>
                  <Row $alignItems="center">
                    {icon && <Icon icon={icon} $size="L" $marginRight="XS" />}
                    {label}
                  </Row>
                </MenuItem>
              )
            })}
          </StyledSelect>
        </StyledFormControl>
        {loading && (
          <div className={styles.spinnerWrapper}>
            <Spinner $size="15px" $borderWidth={3} />
          </div>
        )}
        {icon && (
          <Absolute
            style={{ transform: "translateY(-50%)" }}
            $left={baseTheme.space.S}
            $top="50%"
            $display="flex"
            $alignItems="center"
          >
            <Icon icon={icon} $size="15px" />
          </Absolute>
        )}
      </div>
      {error && <ErrorMessage>{error?.message ?? "To pole jest wymagane"}</ErrorMessage>}
    </div>
  )
}

export default Select
